import * as m from '@/paraglide/messages';
import { cn } from '@listening/utils/react-helpers';
import {
  fieldSurface,
  menuSurface,
} from '@/components/web-fe/design-v2/surfaces';
import { getLocale, locales, setLocale } from '@/paraglide/runtime';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@listening/frontend/components/radix/select';
import { SettingsRowsSkeleton } from '@/components/web-fe/design-v2/skeletons';
import { createFileRoute } from '@tanstack/react-router';
import {
  SettingRow,
  SettingsCard,
  SettingsPaneHeader,
} from '@/components/web-fe/design-v2/settings-shell';
import {
  type ThemePreference,
  useTheme,
} from '@/components/shared-ui/theme-provider';
import { useUpdateUserPreferencesMutation } from '@listening/frontend/query/user-hooks';
import {
  type LibrarySortMode,
  librarySortModes,
  useLibrarySortMode,
  useSetLibrarySortMode,
} from '@listening/frontend/stores/library-sort-store';
import { sortLabel } from '@/components/web-fe/design-v2/library-sort';

export const Route = createFileRoute(
  '/app/_authenticated/_dashboard/settings/appearance',
)({
  component: Page,
  pendingComponent: SettingsRowsSkeleton,
  pendingMs: 0,
});

type Locale = (typeof locales)[number];

const localeLabel: Record<Locale, () => string> = {
  en: () => m.common_english(),
  es: () => m.common_spanish(),
};

function Page() {
  // `themePref` is what the user chose ('system' included); `theme` is what that
  // resolves to. The control has to bind to the preference, not the resolution.
  const { setThemePref, themePref } = useTheme();
  const currentLocale = getLocale();
  const { mutate } = useUpdateUserPreferencesMutation();
  // The same value the library/folder sort dropdowns bind to, so changing it
  // here is indistinguishable from changing it there.
  const librarySortMode = useLibrarySortMode();
  const setLibrarySortMode = useSetLibrarySortMode();

  const themeOptions: Array<{ value: ThemePreference; label: string }> = [
    { value: 'light', label: m.common_light() },
    { value: 'dark', label: m.common_dark() },
    { value: 'system', label: m.common_system() },
  ];

  return (
    <>
      <SettingsPaneHeader title={m.settings_v2_appearance_language()} />
      <SettingsCard>
        <SettingRow
          title={m.settings_v2_theme()}
          description={m.settings_v2_theme_description()}
        >
          <Select
            value={themePref}
            onValueChange={(next) => {
              setThemePref(next as ThemePreference);
            }}
          >
            <SelectTrigger
              className={cn('h-9 w-[180px] text-[13px]', fieldSurface)}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={menuSurface}>
              {themeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SettingRow>
        <SettingRow
          title={m.settings_v2_library_sort()}
          description={m.settings_v2_library_sort_description()}
        >
          <Select
            value={librarySortMode}
            onValueChange={(next) => {
              setLibrarySortMode(next as LibrarySortMode);
            }}
          >
            <SelectTrigger
              className={cn('h-9 w-[180px] text-[13px]', fieldSurface)}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={menuSurface}>
              {librarySortModes.map((mode) => (
                <SelectItem key={mode} value={mode}>
                  {sortLabel(mode)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SettingRow>
        <SettingRow
          title={m.settings_language_title()}
          description={m.settings_v2_language_description()}
        >
          <Select
            value={currentLocale}
            onValueChange={(next) => {
              const nextLocale: Locale = next === 'es' ? 'es' : 'en';
              if (nextLocale === currentLocale) return;
              void setLocale(nextLocale);
              mutate({ most_recent_language: nextLocale });
            }}
          >
            <SelectTrigger
              className={cn('h-9 w-[180px] text-[13px]', fieldSurface)}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={menuSurface}>
              {locales.map((locale) => (
                <SelectItem key={locale} value={locale}>
                  {localeLabel[locale]()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SettingRow>
      </SettingsCard>
    </>
  );
}
